import { SURROUND_TEXTURE_DIMENSION } from '@rta/rta-blueprint-library';

import type { FlatTrimGroup, FlatTrimLayout, FlatTrimPiece } from './types';
import {
	TRIM_DIMENSION_LABEL_GAP,
	TRIM_DIMENSION_LABEL_HEIGHT_FACTOR,
	TRIM_HORIZONTAL_SPACING,
	TRIM_NAME_GAP,
	TRIM_NAME_LINE_HEIGHT,
	TRIM_STEP_DIMENSION_GAP
} from './flat-trim-constants';

export type GetFlatTrimLayoutParams = {
	groups: Array<FlatTrimGroup>;
	dimensionFontSize: number;
	cornerScale: number;
};

export const getFlatTrimLayout = (params: GetFlatTrimLayoutParams): FlatTrimLayout => {
	const { groups, dimensionFontSize, cornerScale } = params;

	let currentX = 0;
	const centerXs = groups.map((group) => {
		const centerX = currentX + (group.width / 2);
		currentX += group.width + TRIM_HORIZONTAL_SPACING;
		return centerX;
	});

	const totalWidth = currentX - TRIM_HORIZONTAL_SPACING;
	const maxHeight = Math.max(...groups.map(({ leftHeight, rightHeight }) => Math.max(leftHeight, rightHeight)));

	// pieces sit on a shared baseline, the whole row centered on the origin
	const rowOffsetX = -totalWidth / 2;
	const baselineY = maxHeight / 2;
	const labelTopY = baselineY - maxHeight;

	// the title block sits above the band reserved for the tallest piece's 1.5" step dimension
	const nameSuffixY = labelTopY - TRIM_STEP_DIMENSION_GAP - TRIM_DIMENSION_LABEL_GAP -
		(dimensionFontSize * TRIM_DIMENSION_LABEL_HEIGHT_FACTOR) - TRIM_NAME_GAP;

	// the corner pattern tiles in user space, so each piece is drawn in its own frame centred on a
	// tile -- otherwise pieces at different x each sample a different part of the texture
	const patternSize = SURROUND_TEXTURE_DIMENSION * cornerScale;
	const patternOriginX = patternSize / 2;

	const pieces: Array<FlatTrimPiece> = groups.map((group, groupIndex) => {
		const { leftHeight, rightHeight, name } = group;

		const tallHeight = Math.max(leftHeight, rightHeight);
		const suffixStart = name.indexOf(' (');
		const nameSuffix = suffixStart < 0 ? '' : name.slice(suffixStart + 1);

		return {
			...group,
			x: rowOffsetX + centerXs[groupIndex],
			tallHeight,
			patternOriginX,
			patternOriginY: (patternSize + tallHeight) / 2,
			namePrimary: suffixStart < 0 ? name : name.slice(0, suffixStart),
			nameSuffix,
			namePrimaryY: nameSuffixY - (nameSuffix ? TRIM_NAME_LINE_HEIGHT : 0)
		};
	});

	return { pieces, baselineY, nameSuffixY };
};
